<template>
    <HeaderCardsLayout
        v-if="event"
        :logo-url="logoState === 'Processing' ? (pendingLogoUrl ?? '') : (iconUrl ?? '')"
        :logo-alt="`Logo ${event.nameShort}`"
        :logo-state="logoState"
        variant="Logo"
    >
        <template #headline>{{ event.nameShort }}</template>
        <template #headline-extra>
            <BadgeDropDown
                :label="$t('event-status-label')"
                class="text-base font-normal"
                :current-value="getEventStatusItem(event.status as EventStatus)"
                :values="getEventStatusItems(event.status as EventStatus)"
                :status="statusDropDownStatus"
                :tooltip="changeStatusDisabledExplanation"
                size="Large-40"
                @change-value="(status: string) => updateEventStatus(status as EventStatus)"
            />
        </template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="event.nameLong">
                <BreadcrumbLink :target="{ name: 'events' }">
                    <BallotIcon class="mr-1 shrink-0" />
                    {{ $t('event-wiki') }}
                </BreadcrumbLink>
                <BreadcrumbLink
                    v-if="event.series"
                    :target="{ name: 'event-series-overview', params: { seriesId: event.series.id } }"
                >
                    <SingleEventIcon v-if="event.series.singleEvent" class="mr-1 shrink-0" />
                    {{ event.series.nameLong }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>
        <template #buttons>
            <LinkButton
                size="Base"
                variant="Info-Strong"
                :to="{ name: 'event-allocations-edit', params: { eventId } }"
                :disabled-explanation="editAllocationsDisabledExplanation"
            >
                <template #icon>
                    <EditIcon class="size-5" />
                </template>
                {{ $t('edit-allocations') }}
            </LinkButton>
            <Button
                variant="Danger-Strong"
                size="Base"
                :disabled-explanation="deleteDisabledExplanation"
                @click="deleteEvent"
            >
                <template #icon>
                    <DeleteForeverIcon class="size-5" />
                </template>
                {{ $t('delete') }}
            </Button>
        </template>
        <template #tab-navigation>
            <TabNavigation
                :headline="$t('tab')"
                :items="tabNavigationItems"
                tab-panel-id="eventTabs"
                :tabs-disabled-text="tabsDisabledText"
                @focus-tab-panel="focusTabPanel"
            />
        </template>
        <template #body>
            <PaddedContent>
                <TabPanel :id="tabPanelId" ref="tabPanelRef" :headline="activeTabHeadline">
                    <RouterView />
                </TabPanel>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import BadgeDropDown from '@/components/BadgeDropDown/BadgeDropDown.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import BallotIcon from '@/components/Icon/BallotIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import EditIcon from '@/components/Icon/EditIcon.vue';
import SingleEventIcon from '@/components/Icon/SingleEventIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import TabNavigation from '@/components/TabNavigation/TabNavigation.vue';
import TabPanel from '@/components/TabNavigation/TabPanel.vue';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { $t } from '@/plugins/fluent';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { nanoid } from 'nanoid';
import { computed, provide, ref } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useEvent } from '../composables/useEvent';
import { useEventLogoPolling } from '../composables/useLogoUploadPolling';
import { usePendingLogo } from '../composables/usePendingLogo';
import { getEventStatusItem, getEventStatusItems } from '../event';
import { eventKey } from '../keys';
import { EventStatus } from '../types';

const props = defineProps<{ eventId: string }>();
const fluent = useFluent();

const tabPanelRef = ref();
const eventId = computed(() => props.eventId);
const isAdmin = auth.isAdmin();

const {
    event,
    iconUrl,
    updateEventStatusError,
    updateEventStatusLoading,
    updateEventStatus,
    updateEvent,
    deleteEvent,
    createEventMultiLink,
    hasAllocations,
    refetchHasAllocations,
} = useEvent(eventId);

const logoUploadStatus = computed(() => event.value?.iconUploadStatus);
useEventLogoPolling(eventId, logoUploadStatus);
const { logoState, pendingLogoUrl } = usePendingLogo(logoUploadStatus);

provide(eventKey, {
    event,
    iconUrl,
    updateEventStatusError,
    updateEventStatusLoading,
    updateEventStatus,
    updateEvent,
    deleteEvent,
    createEventMultiLink,
    hasAllocations,
    refetchHasAllocations,
    logoState,
    pendingLogoUrl,
});

const { activeCard, getDisabledExplanation } = useActiveCard();

const tabPanelId = nanoid();

const deleteDisabledExplanation = computed(() => {
    if (!isAdmin) {
        return fluent.$t('no-permission-to-edit');
    }
    if (hasAllocations.value === true) {
        return fluent.$t('event-has-allocations');
    }
});
const editAllocationsDisabledExplanation = computed(() => {
    return isAdmin ? undefined : fluent.$t('no-permission-to-edit');
});
const tabsDisabledText = computed(() => fluent.$t('tabs-disabled', { name: activeCard.value.label }));
const changeStatusDisabledExplanation = computed(() => {
    return isAdmin ? undefined : fluent.$t('no-permission-to-edit');
});

const statusDropDownStatus = computed(() => {
    if (changeStatusDisabledExplanation.value) return 'Disabled';
    if (updateEventStatusLoading.value) return 'Loading';
    if (updateEventStatusError.value) return 'Retry';
    return 'Default';
});

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) return false;
    }
});

const isAllocationsTabDisabled = computed(() => Boolean(getDisabledExplanation('event-allocations-tab').value));
const isMasterDataTabDisabled = computed(() => Boolean(getDisabledExplanation('event-masterdata-tab').value));
const isOrganisationTabDisabled = computed(() => Boolean(getDisabledExplanation('event-organisation-tab').value));
const isLinksTabDisabled = computed(() => Boolean(getDisabledExplanation('event-links-tab').value));
const isEventVariantsTabDisabled = computed(() => Boolean(getDisabledExplanation('event-variants-tab').value));

const tabNavigationItems = ref([
    {
        target: { name: 'event-masterdata-tab' },
        routeName: 'event-masterdata-tab',
        label: $t('master-data'),
        isDisabled: isMasterDataTabDisabled,
    },
    {
        target: { name: 'event-allocations-tab' },
        routeName: 'event-allocations-tab',
        label: $t('allocations'),
        isDisabled: isAllocationsTabDisabled,
    },
    {
        target: { name: 'event-variants-tab' },
        routeName: 'event-variants-tab',
        label: $t('event-variants'),
        isDisabled: isEventVariantsTabDisabled,
    },
    {
        target: { name: 'event-organisation-tab' },
        routeName: 'event-organisation-tab',
        label: $t('organisational-structure'),
        isDisabled: isOrganisationTabDisabled,
    },
    {
        target: { name: 'event-links-tab' },
        routeName: 'event-links-tab',
        label: $t('links'),
        isDisabled: isLinksTabDisabled,
    },
]);

const route = useRoute();
const activeTabHeadline = computed(() => {
    return $t('tab-panel', {
        activeTabName: tabNavigationItems.value.find((item) => item.routeName === route.name)?.label ?? '',
    });
});

function focusTabPanel() {
    tabPanelRef.value.focus();
}

useHeadSafe({
    title: () => `${event.value?.nameShort} - ${event.value?.series?.nameShort} - ${fluent.$t('event-wiki')}`,
});
</script>
